import { Check, CheckCircle, ErrorOutline, Visibility } from "@mui/icons-material";
import { CircularProgress, Grid, IconButton, InputAdornment } from "@mui/material";
import bgImage from "assets/images/logos/logo_icon.png";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";
import { changePasswordForm, changePasswordValidations } from "layouts/pages/account/settings/components/ChangePassword";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { errorAlert, successAlert } from "utils/Functions";
import { baseURL } from "utils/WebService";


export default function RecoverAccount() {
    const { newPassword, newPasswordConfirmation } = changePasswordForm.formField;
    const { token } = useParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmationPassword, setShowConfirmationPassword] = useState(false);

    const passwordRequirements = [
        { label: "Mínimo de 6 caracteres", test: (password) => password.length >= 6 },
        { label: "Uma letra maiúscula", test: (password) => /[A-Z]/.test(password) },
        { label: "Um caractere especial", test: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password) },
        { label: "Um número", test: (password) => /\d/.test(password) },
    ];

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };
    const toggleConfirmationPasswordVisibility = () => {
        setShowConfirmationPassword(!showConfirmationPassword);
    };
    const navigate = useNavigate();

    const checkPasswordRequirements = (password) => {
        return passwordRequirements.map((requirement) => ({
            label: requirement.label,
            satisfied: requirement.test(password),
        }));
    };

    const changePassword = (values, actions) => {
        const { newPassword } = values;
        actions.setSubmitting(true);
        fetch(`${baseURL}/users/change-password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token,
            },
            body: JSON.stringify({
                password: newPassword
            }),
        }).then(res => {
            if (!res.ok) {
                if (res.status == 403) {
                    throw new Error('Token expirado');
                } else {
                    throw new Error('Ocorreu um erro ao processar sua solicitação.');
                }
            }
            return res
        }).then(() => successAlert("Acesso alterado com sucesso!", () => {
            actions.setTouched({});
            actions.resetForm();
            actions.setSubmitting(false);
            navigate("/login");
        })).catch((error) => {
            if (error.message !== 'Token expirado') {
                console.log(error)
                errorAlert("Algo de errado aconteceu! Tente novamente em breve.", () => actions.setSubmitting(false));
            } else {
                errorAlert("Token expirado! Você precisa solicitar novamente, basta ir em esqueci a senha e informar seu e-mail.", () => {
                    actions.setSubmitting(false);
                    navigate("/login");
                });
            }
        });

    }


    return (
        <IllustrationLayout
            title={"Recuperação de Acesso"}
            description="Configure sua nova senha"
            illustration={bgImage} >
            <Formik
                initialValues={{
                    newPassword: "",
                    newPasswordConfirmation: "",
                }}
                validationSchema={changePasswordValidations}
                onSubmit={changePassword}>
                {({ values, errors, touched, isSubmitting }) => {
                    const {
                        newPassword: newPasswordV,
                        newPasswordConfirmation: newPasswordConfirmationV,
                    } = values;
                    const passwordChecks = checkPasswordRequirements(newPasswordV);
                    return (
                        <Form id={changePasswordForm.formId} autoComplete="off">
                            <MDBox>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <FormField
                                            fullWidth
                                            type={showPassword ? "text" : "password"}
                                            label={newPassword.label}
                                            name={newPassword.name}
                                            value={newPasswordV}
                                            placeholder={newPassword.placeholder}
                                            InputProps={{
                                                startAdornment: (
                                                    <>
                                                        {errors.newPassword && touched.newPassword ? (
                                                            <InputAdornment position="start">
                                                                <ErrorOutline style={{ color: 'red' }} />
                                                            </InputAdornment>
                                                        ) : newPasswordV.length > 0 && !errors.newPassword ? (
                                                            <InputAdornment position="start">
                                                                <CheckCircle style={{ color: 'green' }} />
                                                            </InputAdornment>
                                                        ) : null}
                                                    </>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => togglePasswordVisibility()} edge="end" style={{ backgroundColor: 'transparent' }}>
                                                            <Visibility style={{ color: '#7b809a' }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                                autoComplete: ""
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormField
                                            fullWidth
                                            type={showConfirmationPassword ? "text" : "password"}
                                            label={newPasswordConfirmation.label}
                                            name={newPasswordConfirmation.name}
                                            value={newPasswordConfirmationV}
                                            placeholder={newPasswordConfirmation.placeholder}
                                            InputProps={{
                                                startAdornment: (
                                                    <>
                                                        {errors.newPasswordConfirmation && touched.newPasswordConfirmation ? (
                                                            <InputAdornment position="start">
                                                                <ErrorOutline style={{ color: 'red' }} />
                                                            </InputAdornment>
                                                        ) : newPasswordConfirmationV.length > 0 && !errors.newPasswordConfirmation ? (
                                                            <InputAdornment position="start">
                                                                <CheckCircle style={{ color: 'green' }} />
                                                            </InputAdornment>
                                                        ) : null}
                                                    </>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => toggleConfirmationPasswordVisibility()} edge="end" style={{ backgroundColor: 'transparent' }}>
                                                            <Visibility style={{ color: '#7b809a' }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                                autoComplete: ""
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MDButton
                                            variant="contained"
                                            color="info"
                                            size="large"
                                            fullWidth
                                            disabled={isSubmitting}
                                            type="submit"
                                            endIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : null}>
                                            {isSubmitting ? "" : "Alterar Acessar"}
                                        </MDButton>
                                    </Grid>
                                </Grid>
                                <MDBox mt={2} mb={1}>
                                    <MDTypography variant="h5">Requerimentos da Senha</MDTypography>
                                    <MDBox component="ul" m={0} mt={2} pl={2.5} mb={{ xs: 8, sm: 0 }}>
                                        {passwordChecks.map((item, key) => (
                                            <MDBox
                                                key={key}
                                                component="li"
                                                color="text"
                                                fontSize="1.25rem"
                                                lineHeight={1}
                                                style={{ listStyleType: "none" }}>
                                                <MDTypography
                                                    variant="button"
                                                    color={item.satisfied ? "success" : "text"}
                                                    fontWeight="regular"
                                                    verticalAlign="middle"
                                                    style={{ display: "flex", alignItems: "center" }}
                                                >
                                                    {item.satisfied ? (
                                                        <Check style={{ marginRight: 8 }} />
                                                    ) : (
                                                        <span style={{ marginRight: 16 }}>•</span>
                                                    )}
                                                    {item.label}
                                                </MDTypography>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                </MDBox>

                            </MDBox>
                        </Form>)
                }}
            </Formik>
        </IllustrationLayout >
    )
}